/**
 * Freemium plan definitions.
 *
 * Limits are intentionally `null` (unlimited) for now — the plumbing is in
 * place so a paid tier can be switched on the day Dodo Payments goes live
 * without touching call sites.
 */
export type PlanId = "free" | "premium";

export type PlanLimits = {
  /** null = unlimited */
  tests: number | null;
  revisionChapters: number | null;
  aiRequestsPerDay: number | null;
};

export type Plan = {
  id: PlanId;
  name: string;
  tagline: string;
  priceLabel: string;
  priceNote: string;
  limits: PlanLimits;
  features: string[];
  /** Dodo Payments product id — filled in when billing is switched on. */
  dodoProductId: string | null;
};

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: "free",
    name: "Free",
    tagline: "Everything you need to start tracking today.",
    priceLabel: "₹0",
    priceNote: "Free forever",
    limits: { tests: null, revisionChapters: null, aiRequestsPerDay: null },
    features: [
      "Syllabus & lecture tracking",
      "Spaced-repetition revision planner",
      "Test tracker with score history",
      "Question practice targets",
      "Basic toolkit, simulations & labs",
      "Progress synced to your account",
    ],
    dodoProductId: null,
  },
  premium: {
    id: "premium",
    name: "Premium",
    tagline: "For aspirants who want the full engine.",
    priceLabel: "Coming soon",
    priceNote: "Launching with Dodo Payments",
    limits: { tests: null, revisionChapters: null, aiRequestsPerDay: null },
    features: [
      "Unlimited tests & revision plans",
      "AI study planner",
      "Advanced performance analytics",
      "Premium toolkit modules",
      "Premium simulations & virtual labs",
      "Premium question features & bookmarks",
      "Unlimited multi-device sync",
      "Priority support",
    ],
    dodoProductId: null,
  },
};

/** True when the plan allows another item of this kind. */
export function withinLimit(
  plan: PlanId,
  key: keyof PlanLimits,
  currentCount: number,
): boolean {
  const limit = PLANS[plan].limits[key];
  return limit === null || currentCount < limit;
}
